import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import { AlertContext } from '../contexts/alert-context';
import type { Receipt } from '../data/receipt';
import { receiptsQueryOptions } from '../query/query-options';
import { storeLayout } from '../router';
import { useExceptionLogs } from './exception-logs';
import { type FuseSearchField, useFuseSearch } from './use-fuse-search';

export function useReceipts() {
  const { addErrorAlert } = useContext(AlertContext);
  const { store, receiptService } = storeLayout.useRouteContext();

  const query = useQuery({
    ...receiptsQueryOptions(store.id),
    queryFn: () => {
      return receiptService.getReceipts(store.id).catch((error: Error) => {
        addErrorAlert(error.message, { cause: error });
        throw error;
      });
    },
  });

  const receipts = useMemo(() => query.data ?? [], [query.data]);
  const loading = receipts.length === 0 && query.isLoading;

  return { receipts, loading };
}

export function useExceptionInvoiceNumbers(): Set<number> {
  const { exceptionLogs } = useExceptionLogs();

  return useMemo(
    () =>
      new Set(
        exceptionLogs.filter((log) => log.invoice_number > 0).map((log) => log.invoice_number)
      ),
    [exceptionLogs]
  );
}

export const searchPlaceholders = [
  'Search by invoice number',
  'Search by product brand & description',
  'Search by barcode',
  'Search by clerk initials',
];

const receiptSearchFields: FuseSearchField<Receipt>[] = [
  { name: 'invoice_number', exact: true },
  { name: 'initials' },
  { name: 'items.product' },
  { name: 'items.barcode' },
];

export function useReceiptSearch(receipts: Receipt[], query: string) {
  const matchesSearch = useFuseSearch(query, receipts, receiptSearchFields, (r) =>
    String(r.invoice_number)
  );

  return useMemo(() => receipts.filter(matchesSearch), [receipts, matchesSearch]);
}
